import type { Experience } from "@repo/contract/training";
import {
  "onboarding.experience_consistent_for.question" as consistentQuestion,
  "onboarding.experience.question" as experienceQuestion,
  "onboarding.experience_stopped_ago.question" as stoppedQuestion,
  "onboarding.experience_trained_for.question" as trainedQuestion,
} from "@repo/i18n";
import { useCallback } from "react";
import { ChoiceList } from "../../../ui/controls";
import {
  type ConsistentFor,
  consistentForOptions,
  type ExperienceStatus,
  experienceStatusOptions,
  type StoppedAgo,
  stoppedAgoOptions,
  type TrainedFor,
  trainedForOptions,
} from "./options";
import { FieldError, FieldLabel } from "./parts";

/**
 * The experience tree as one draft: every branch's follow-up is kept while the
 * person flips between answers, and only the chosen branch is sent
 * ({@link experienceOf}) — the contract is a discriminated union and refuses a
 * `never` that also says how long ago.
 */
export interface ExperienceDraft {
  readonly consistentFor: ConsistentFor | null;
  readonly status: ExperienceStatus | null;
  readonly stoppedAgo: StoppedAgo | null;
  readonly trainedFor: TrainedFor | null;
}

export function experienceDraftFrom(
  stored: Experience | null
): ExperienceDraft {
  return {
    consistentFor: stored?.status === "current" ? stored.consistentFor : null,
    status: stored?.status ?? null,
    stoppedAgo: stored?.status === "stopped" ? stored.stoppedAgo : null,
    trainedFor: stored?.status === "stopped" ? stored.trainedFor : null,
  };
}

/** The branch the draft stands for, or `null` while a follow-up is missing. */
export function experienceOf(draft: ExperienceDraft): Experience | null {
  switch (draft.status) {
    case "never":
      return { status: "never" };
    case "current":
      return draft.consistentFor === null
        ? null
        : { consistentFor: draft.consistentFor, status: "current" };
    case "stopped":
      return draft.stoppedAgo === null || draft.trainedFor === null
        ? null
        : {
            status: "stopped",
            stoppedAgo: draft.stoppedAgo,
            trainedFor: draft.trainedFor,
          };
    default:
      return null;
  }
}

export interface ExperienceFormProps {
  error?: string;
  onChange: (value: ExperienceDraft) => void;
  value: ExperienceDraft;
}

/**
 * "Você já treinou com pesos antes?" and the follow-ups its answer opens —
 * A2's `experience`, `stoppedAgo` and `trainedFor` sheets in one body.
 */
export function ExperienceForm({
  value,
  onChange,
  error,
}: ExperienceFormProps) {
  const setStatus = useCallback(
    (status: ExperienceStatus) => onChange({ ...value, status }),
    [onChange, value]
  );
  const setConsistent = useCallback(
    (consistentFor: ConsistentFor) => onChange({ ...value, consistentFor }),
    [onChange, value]
  );
  const setStopped = useCallback(
    (stoppedAgo: StoppedAgo) => onChange({ ...value, stoppedAgo }),
    [onChange, value]
  );
  const setTrained = useCallback(
    (trainedFor: TrainedFor) => onChange({ ...value, trainedFor }),
    [onChange, value]
  );
  return (
    <div className="flex flex-col gap-6">
      <ChoiceList
        label={experienceQuestion()}
        onValueChange={setStatus}
        options={experienceStatusOptions()}
        value={value.status}
      />
      {value.status === "current" ? (
        <div>
          <FieldLabel>{consistentQuestion()}</FieldLabel>
          <ChoiceList
            label={consistentQuestion()}
            onValueChange={setConsistent}
            options={consistentForOptions()}
            value={value.consistentFor}
          />
        </div>
      ) : null}
      {value.status === "stopped" ? (
        <>
          <div>
            <FieldLabel>{stoppedQuestion()}</FieldLabel>
            <ChoiceList
              label={stoppedQuestion()}
              onValueChange={setStopped}
              options={stoppedAgoOptions()}
              value={value.stoppedAgo}
            />
          </div>
          <div>
            <FieldLabel>{trainedQuestion()}</FieldLabel>
            <ChoiceList
              label={trainedQuestion()}
              onValueChange={setTrained}
              options={trainedForOptions()}
              value={value.trainedFor}
            />
          </div>
        </>
      ) : null}
      {error ? <FieldError>{error}</FieldError> : null}
    </div>
  );
}
